import ReactGA from 'react-ga4';
import Hotjar from '@hotjar/browser';

import {
    Environment,
    getEnvironmentByHostname,
    isDevelopment,
    isEphemeralHostname,
    isProduction,
} from './environmentByHostname';
import { filterSentryEvent } from './sentryEventFilters';
import { withSentry } from './withSentry';

export type HotjarAttributes = Record<string, string | number | boolean | null>;
declare global {
    interface Window {
        hj?: (
            command: 'identify',
            userId: string | number | null,
            attributes: HotjarAttributes
        ) => void;
    }
}

interface AnalyticsConfig {
    googleAnalyticsId: string;
    hotjarSiteId: number;
}

const ANALYTICS_CONFIG: Record<Environment, AnalyticsConfig> = {
    [Environment.Production]: {
        googleAnalyticsId: 'G-JQY5CBWB5H',
        hotjarSiteId: 6521496,
    },
    [Environment.Staging]: {
        googleAnalyticsId: 'G-QXK80Y1BF4',
        hotjarSiteId: 6524405,
    },
    [Environment.Development]: {
        googleAnalyticsId: '',
        hotjarSiteId: 0,
    },
} as const;

const HOTJAR_VERSION = 6;
const SENTRY_DSN =
    'https://648d332798a14fa6b753e3d86fb9c4aa@o4505509385928704.ingest.sentry.io/4505509388222465';

export function initializeAnalyticsTools(): void {
    // Localhost and per-PR previews send nothing (HMR / test traffic).
    if (isEphemeralHostname() || isDevelopment()) {
        return;
    }

    const environment = getEnvironmentByHostname();
    const config = ANALYTICS_CONFIG[environment];

    // Google Analytics (GA4) Initialization
    if (config.googleAnalyticsId) {
        ReactGA.initialize([
            {
                trackingId: config.googleAnalyticsId,
            },
        ]);
    }

    // Hotjar Initialization
    if (config.hotjarSiteId > 0) {
        Hotjar.init(config.hotjarSiteId, HOTJAR_VERSION);
    }

    // Sentry is production-only. Staging/local set SENTRY_ENABLED=false;
    // hostname is the browser equivalent (the flag is not in the JS bundle).
    if (!isProduction()) {
        return;
    }

    // Sentry Initialization
    withSentry((s) => {
        const { browserTracingIntegration, replayIntegration } = s;
        s.init({
            dsn: SENTRY_DSN,
            integrations: [
                browserTracingIntegration(),
                replayIntegration({
                    maskAllText: false,
                    maskAllInputs: false,
                    blockAllMedia: false,
                }),
            ],
            environment: environment,
            beforeSend: filterSentryEvent,

            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: [
                /^http:\/\/localhost/,
                /^https:\/\/web\.optoscale\.dev/,
                /^https:\/\/web\.optoscale\.no/,
                /^https:\/\/api\.optoscale\.dev/,
                /^https:\/\/api\.optoscale\.no/,
            ],

            tracesSampleRate: 0.2,
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
        });
    });
}

export function setHotjarUserAttributes(
    userId: string | number | null,
    attributes: HotjarAttributes
): void {
    try {
        const environment = getEnvironmentByHostname();
        const config = ANALYTICS_CONFIG[environment];
        if (!(config.hotjarSiteId > 0)) return;

        // Hotjar.identify only forwards to window.hj, but its types reject numeric ids and null
        // attribute values that hj itself accepts, so the call goes to hj directly.
        window.hj?.('identify', userId ?? null, attributes);
    } catch {
        // No-op: analytics should never break the app
    }
}

export function setHotjarUserFromMe(me: {
    isOptoScaleAdmin: boolean;
    state: {
        user: {
            id: number | string;
        } | null;
        clients?: { id: number | string }[];
    };
}): void {
    const userId = me.state.user?.id ?? null;
    const primaryClient =
        me.state.clients && me.state.clients.length > 0 ? me.state.clients[0] : null; // An user may have multiple clients
    const primaryClientId = primaryClient?.id ?? null;
    setHotjarUserAttributes(userId, {
        'userId': userId,
        'primaryClientId': primaryClientId,
        'isOptoscaleAdmin': me.isOptoScaleAdmin,
        'isNotOptoscaleAdmin': !me.isOptoScaleAdmin,
    });
}
